import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

/**
 * Global inbox notification controller — active on all dashboard pages.
 *
 * Subscribes to InboxChannel and handles:
 * - Playing notification sounds for new conversations/messages
 * - Showing browser notifications (when agent is on a different page)
 * - Updating sidebar inbox badge
 */
export default class extends Controller {
  static values = {
    organizationId: Number
  };

  connect() {
    if (!this.organizationIdValue) return;
    if (this.subscription) return;

    this.subscription = consumer.subscriptions.create(
      {
        channel: "InboxChannel",
        organization_id: this.organizationIdValue
      },
      {
        connected: () => this.handleConnected(),
        disconnected: () => this.handleDisconnected(),
        received: (data) => this.handleReceived(data)
      }
    );
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  handleConnected() {}

  handleDisconnected() {}

  handleReceived(data) {
    const onInboxPage = window.location.pathname.includes("/inbox");

    switch (data.type) {
    case "new_conversation":
      this.notifyAgent(data, onInboxPage);
      this.updateSidebarCount(data);
      break;
    case "new_message":
      // Only notify for inbound messages (from customer)
      if (data.direction === "inbound") {
        this.notifyAgent(data, onInboxPage);
        this.updateSidebarCount(data);
      }
      break;
    case "unread_counts":
      this.updateSidebarBadge(data.total);
      break;
    }
  }

  // Play notification sound and show browser notification
  notifyAgent(data, onInboxPage) {
    this.playSound();

    // Only show browser notification if agent is on a different page
    if (!onInboxPage && this.browserNotificationSupported()) {
      this.showBrowserNotification(data);
    }
  }

  playSound() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1000, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch {
      // Audio not supported — silently ignore
    }
  }

  browserNotificationSupported() {
    return "Notification" in window && Notification.permission !== "denied";
  }

  async showBrowserNotification(data) {
    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;
    }

    const title = data.channel === "chat"
      ? `New chat from ${data.customer_name || "a visitor"}`
      : `New ${data.channel} message`;
    const body = data.subject || data.preview || "";

    new Notification(title, {
      body: body,
      icon: "/kallglot_iconography.png",
      tag: `inbox-${data.conversation_id}`
    });
  }

  updateSidebarCount(data) {
    const { total } = data;

    // Update main inbox badge
    const mainBadge = document.querySelector(`[data-nav="inbox"] [data-unread-badge]`);
    this.setBadgeCount(mainBadge, total);
  }

  updateSidebarBadge(total) {
    const mainBadge = document.querySelector(`[data-nav="inbox"] [data-unread-badge]`);
    this.setBadgeCount(mainBadge, total);
  }

  setBadgeCount(element, count) {
    if (!element) return;
    if (count > 0) {
      element.textContent = count;
      element.classList.remove("hidden");
    } else {
      element.classList.add("hidden");
    }
  }
}
